/**
 * BossQuoteRequest Module
 * Handles the multi-step quote request form including validation, geocoding, and dealer locator
 */
const BossQuoteRequest = (() => {
    'use strict';

    // ==================== Private Variables ====================      
    let nextClicked = false

    // ==================== Selectors ====================
    const selectors = {
        // Form steps
        productCollapse: '#producttype-collapse',
        personalCollapse: '#personalinfo-collapse',
        dealerCollapse: '#dealerinfo-collapse',
        summaryCollapse: '#summarystep-collapse',

        // Form fields
        country: '#quote_country',
        address1: '#quote_address1',
        locality: '#quote_locality',
        region: '#quote_region',
        postcode: '#quote_postcode',
        email: '#quote_email',
        latitude: '#quote_latitude',
        longitude: '#quote_longitude',
        dealerId: '#dealerId',
        dealerandLocationId: 'input[name="quote.dealerandlocationid[]"]',
        preferredContactMethod: '#quote_preferredcontactmethod',
        pagerItems: 'a[data-pager="true"]',
        collapseToggle: 'a[data-toggle="collapse"]',

        // Display elements
        dealerList: '#dealer-list',
        pager: '#dealerinfo-collapse ul.pager',
        stateFormGroup: '#form-group-state',
        dealersPageIndex: '#dealersmetadata_pageindex',

        // Templates
        dealerTemplate: '#dealerTemplate',
        pagerTemplate: '#pagerItemTemplate'
    };

    // ==================== Step Navigation ====================
    const StepNav = {
        /**
         * Activates the next step in the form
         */
        activateStep: function (stepSelector) {
            const step = $(stepSelector || "div.panel-collapse .show").next().addBack();
            if (step.hasClass("disabled") || step.hasClass('show')) {
                return;
            }
            step.parent().find(selectors.collapseToggle).trigger('click');
            step.scrollTop();
        },

        /**
         * Unlock a collapsed section by removing the disabled class
         */
        unlockStep: function (stepSelector) {
            $(stepSelector).toggleClass("disabled", false);
        },

        /**
         * Mark a transition as in-progress
         */
        setTransitioning: function (isTransitioning) {
            nextClicked = isTransitioning;
        }
    };

    // ==================== Validation ====================
    const FormValidator = {
        /**
         * Get the FormValidation instance
         */
        getInstance: function () {
            const formId = SitecoreForm.formValidation().FormId;
            return $('#' + formId).data('formValidation');
        },

        /**
         * Validate personal information fields
         */
        validatePersonalInfo: function () {
            const $fv = this.getInstance();
            const validationPromises = [
                $fv.revalidateField('quote.surname'),
                $fv.revalidateField('quote.country'),
                $fv.revalidateField('quote.address1'),
                $fv.revalidateField('quote.locality'),
                $fv.revalidateField('quote.postcode'),
                $fv.revalidateField('quote.email')
            ];

            const stateSection = $('#form-group-state');
            if (stateSection && !stateSection.hasClass('hidden')) {
                validationPromises.push($fv.revalidateField('quote.region'));
            }

            // Handle phone validation based on preferred contact method
            const prefContact = $(selectors.preferredContactMethod).val();
            if (prefContact === '1') {
                $fv.enableValidator("quote.phone");
                validationPromises.push($fv.revalidateField('quote.phone'));
            } else if (prefContact === '2') {
                $fv.disableValidator("quote.phone");
            }

            return Promise.all(validationPromises);
        },

        /**
         * Validate dealer selection
         */
        validateDealerInfo: function () {
            const $fv = this.getInstance();

            if (!$fv) {
                console.warn('FormValidation not available');
                return Promise.resolve('Valid');
            }

            if (typeof $fv.revalidateField === 'function') {
                return $fv.revalidateField('dealerId');
            }

            // Fallback to simple validation
            const dealerId = $(selectors.dealerId).val();
            return Promise.resolve(dealerId ? "Valid" : "Invalid");
        }
    };

    // ==================== Geocoding & Dealers ====================
    const DealerLocator = {
        /**
         * Get dealers based on location and filters
         */
        getDealers: function (countryCode, latitude, longitude, page) {
            const productType = $('#quote_producttype').val() || "";

            $.ajax({
                url: '/Locator/GetQuoteDealers',
                type: 'GET',
                data: {
                    countryCode: countryCode,
                    latitude: latitude,
                    longitude: longitude,
                    page: page,
                    productType: productType
                },
                success: (response) => this.displayDealers(response),
                error: () => this.handleDealerError()
            });
        },

        /**
         * Display dealers in the UI
         */
        displayDealers: function (response) {
            if (response && response.Dealers && response.Dealers.length > 0) {
                const dealerList = $(selectors.dealerList);
                dealerList.empty();

                $.each(response.Dealers, (index, dealer) => {
                    const dealerHtml = $(selectors.dealerTemplate).tmpl(dealer);
                    dealerList.append(dealerHtml);
                });

                if (response.PaginationData) {
                    this.displayPager(response.PaginationData);
                }
            } else {
                console.log('No dealers found');
                $(selectors.dealerList).html('<p>No dealers found in your area.</p>');
            }
        },

        /**
         * Display pagination controls
         */
        displayPager: function (pagination) {
            const pages = [];

            $(selectors.pagerTemplate).template("pagerItem");

            if (pagination) {
                pages.push({ 
                    Text: "&laquo; Previous Dealers", 
                    Class: "previous", 
                    PageIndex: pagination.PageNumber - 1 
                });
                pages.push({ 
                    Text: "More Dealers &raquo;", 
                    Class: "next", 
                    PageIndex: pagination.PageNumber + 1 
                });
            }

            $.tmpl("pagerItem", pages)
                .appendTo($(selectors.pager).empty())
                .click((e) => this.handlePagerClick(e));

            // Disable previous button if on first page
            $(selectors.pagerItems)
                .filter(function () {
                    return $(this).attr("data-page-index") <= 0;
                })
                .attr('href', '')
                .addClass('disabled')
                .attr('disabled', true);

            $(selectors.dealersPageIndex).val(pagination ? pagination.PageNumber : 0);
        },

        /**
         * Handle pager button click
         */
        handlePagerClick: function (e) {
            const countryCode = $(selectors.country).val() || "US";
            const latitude = $(selectors.latitude).val() || "";
            const longitude = $(selectors.longitude).val() || "";
            const pageIndex = $.tmplItem(e.target).data.PageIndex;
            this.getDealers(countryCode, latitude, longitude, pageIndex);
        },

        /**
         * Handle dealer fetch error
         */
        handleDealerError: function () {
            alert('Unable to find dealers for your location. Please try again.');
        },

        getGeocoder: function () {
            if (typeof google === 'undefined' || !google.maps || !google.maps.Geocoder) {
                return null;
            }
            var geocoder = new google.maps.Geocoder();
            return geocoder;
        },
        /**
         * Geocode address and fetch dealers
         */
        geocodeAndFetchDealers: function () {
            const address = FormHelpers.formatAddressString();
            var geocoder = this.getGeocoder();
            geocoder.geocode({ address: address }, (geoResults, status) => {
                if (status === google.maps.GeocoderStatus.OK) {
                    const location = geoResults && geoResults[0] && geoResults[0].geometry 
                        ? geoResults[0].geometry.location 
                        : null;

                    if (location) {
                        const latitude = location.lat();
                        const longitude = location.lng();
                        const countryCode = $(selectors.country).val() || "US";

                        $(selectors.latitude).val(latitude);
                        $(selectors.longitude).val(longitude);

                        this.getDealers(countryCode, latitude, longitude, 1);
                    }

                    StepNav.unlockStep(selectors.dealerCollapse);
                    StepNav.activateStep(selectors.dealerCollapse);
                    StepNav.setTransitioning(false);
                } else {
                    alert('Geocode was not successful for the following reason: ' + status);
                    StepNav.setTransitioning(false);
                }
            });
        }
    };

    // ==================== Form Helpers ====================
    const FormHelpers = {
        /**
         * Format address from form fields
         */
        formatAddressString: function () {
            const address1 = $(selectors.address1).val() || "";
            const locality = $(selectors.locality).val() || "";
            const region = $(selectors.region).val() || "";
            const postcode = $(selectors.postcode).val() || "";
            const country = $(selectors.country).val() || "";

            let address = address1;
            if (locality) address += " " + locality;
            if (region) address += ", " + region;
            if (postcode) address += " " + postcode;
            if (country) address += " " + country;

            return address;
        },

        /**
         * Toggle state/province selection visibility based on country
         */
        toggleStateVisibility: function () {
            const filterValue = $(selectors.country + ' option:selected').attr("data-filter-value") || '';

            if (filterValue === "") {
                $(selectors.stateFormGroup).addClass("hidden");
            } else {
                $(selectors.stateFormGroup).removeClass("hidden");
            }
        }
    };

    // ==================== Event Handlers ====================
    const EventHandlers = {
        /**
         * Initialize all event handlers
         */
        init: function () {
            this.setupStepNavigation();
            this.setupFormFieldHandlers();
            this.setupCollapseHandlers();
            this.setupSubmitFormHandlers();
        },

        /**
         * Setup step navigation (Next button clicks)
         */
        setupStepNavigation: function () {
            // Product to Personal Info
            $(selectors.productCollapse + ' a.next').on('click', () => {
                StepNav.setTransitioning(true);
                StepNav.unlockStep(selectors.personalCollapse);
                StepNav.activateStep(selectors.personalCollapse);
                StepNav.setTransitioning(false);
            });

            // Personal Info to Dealer Info
            $(selectors.personalCollapse + ' a.next').on('click', () => {
                FormValidator.validatePersonalInfo().then((validationResults) => {
                    const isValid = validationResults.indexOf("Invalid") === -1;
                    if (isValid) {
                        StepNav.setTransitioning(true);
                        DealerLocator.geocodeAndFetchDealers();
                    }
                });
            });

            // Dealer Info to Summary
            $(selectors.dealerCollapse + ' a.next').on('click', () => {
                StepNav.setTransitioning(true);
                FormValidator.validateDealerInfo().then((validationResult) => {
                    if (validationResult === "Valid") {
                        StepNav.unlockStep(selectors.summaryCollapse);
                        StepNav.activateStep(selectors.summaryCollapse);
                        StepNav.setTransitioning(false);
                    }
                });
            });
        },

        /**
         * Setup form field change handlers
         */
        setupFormFieldHandlers: function () {
            // Country selection changes
            $(document).on('change', selectors.country, () => {
                FormHelpers.toggleStateVisibility();
            });

            // Dealer selection
            $(document).on('change', selectors.dealerandLocationId, function () {
                const selectedValue = $(this).val() || "";
                const parts = selectedValue.split('-');
                const dealerId = parts[0] || "";
                const locationId = parts[1] || "";

                $('#quote_dealerid').val(dealerId);
                $('#quote_locationid').val(locationId);
                $('#quote_dealername').val($(this).data('dealer-name'));
                $(selectors.dealerId).val(dealerId);
            });
        },

        /**
         * Setup collapse/accordion handlers
         */
        setupCollapseHandlers: function () {
            // Prevent clicking disabled collapse panels
            $(document).on('click', selectors.collapseToggle, (e) => {
                if (!nextClicked) {
                    const panel = $($(e.currentTarget).attr('href'));
                    if (panel && panel.hasClass('disabled')) {
                        e.stopPropagation();
                    }
                }
            });

            // Prevent showing/hiding disabled panels
            $('.collapse').on('show.bs.collapse hide.bs.collapse', (e) => {
                if ($(e.target).hasClass('disabled')) {
                    e.preventDefault();
                }
            });

            // Scroll to active collapse section
            $(document).on('shown.bs.collapse', '#selector-collapse', (e) => {
                const offset = $(e.target).find('.collapse.show').prev('.card-header');
                if (offset.length) {
                    $('html, body').animate({
                        scrollTop: $(offset).offset().top
                    }, 500);
                }
            });

            // Prevent clicking disabled pager items
            $(document).on('click', selectors.pagerItems, (e) => {
                if ($(e.currentTarget).attr('data-page-index') <= 0) {
                    e.preventDefault();
                }
            });
        },

        /**
         * Setup form submit handler
         */
        setupSubmitFormHandlers: function () {
            const myForm = document.querySelector('#quoteRequestForm');

            // Handle form submission with fetch API
            myForm.addEventListener('submit', async (event) => {

                event.preventDefault();

                const formData = new FormData(myForm);

                try {
                    // Send form data to the server
                    const response = await fetch('', {
                        method: 'POST',
                        body: formData,
                        redirect: 'follow'
                    });

                    // Track form submission for analytics
                    if (response.ok && typeof trackQuoteRequestFormSubmit === 'function') {
                        trackQuoteRequestFormSubmit();
                    }

                    // Handle redirection if the response indicates a redirect
                    if (response.redirected) {
                        window.location.href = response.url;
                    }

                    // Handle non-redirect responses (e.g., validation errors)
                    return response;
                } catch (ex) {
                    console.error('Error:', ex)
                }
            });
        }
    };

    // ==================== Public API ====================
    return {
        /**
         * Initialize the module
         */
        init: function () {
            EventHandlers.init();
        }
    };
})();

// Initialize when DOM is ready
$(document).ready(() => {
    BossQuoteRequest.init();
});